import {
  Badge,
  BodyText,
  Card,
  CardInteraction,
  DetailText,
  Flex,
  Heading,
  Link,
  Strong,
} from '@utilitywarehouse/hearth-react';
import {
  ChevronRightSmallIcon,
  TickSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';
import clsx from 'clsx';
import { noop } from 'lodash';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { BundleTileCopyVariant } from '@constants/featureFlags';
import { paths } from '@constants/paths';
import {
  ChargeAmount,
  ProductSubtype,
  ProductType,
} from '@generated/gql/graphql';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useAuthContext } from '@services/Auth';
import { BundleServiceDiscount } from '@src/types/bundle';
import { formatCurrency } from '@utils/format';

import { ProductIndicator } from './ProductIndicator';
import './styles.scss';

interface BundleTileProps {
  isSelected: boolean;
  showCheaperBadge?: boolean;
  productType: ProductType;
  subType?: ProductSubtype;
  price: ChargeAmount;
  discount?: BundleServiceDiscount;
  disabled?: boolean;
  compact?: boolean;
  showMonthLabel?: boolean;
  tbybEligible?: boolean;
  bundleCopyVariant?: BundleTileCopyVariant;
  onClick?: () => void;
  renderDiscount?: () => ReactNode;
}

type DescriptionProps = Omit<BundleTileProps, 'onClick'>;

const getProductLabel = (
  productType: ProductType,
  subType?: ProductSubtype
) => {
  switch (productType) {
    case ProductType.Energy:
      return 'Energy';
    case ProductType.Broadband:
      return 'Broadband';
    case ProductType.Mobile:
      return 'Mobile';
    case ProductType.Insurance:
      if (subType === ProductSubtype.BoilerHomeCover)
        return 'Boiler & Home Cover';
      if (subType === ProductSubtype.IncomeProtector) return 'Income Protector';

      return 'Insurance';
    case ProductType.CashbackCard:
      return 'Cashback Card';
    default:
      return null;
  }
};

type DescriptionOptions = Pick<
  BundleTileProps,
  'subType' | 'disabled' | 'tbybEligible' | 'bundleCopyVariant'
>;

const getDescription = (
  productType: ProductType,
  { subType, disabled, tbybEligible, bundleCopyVariant }: DescriptionOptions
) => {
  const isNewCopy = bundleCopyVariant === 'new';

  if (disabled) {
    switch (productType) {
      case ProductType.Energy:
        return 'Sorry, our energy is currently unavailable in your area';
      case ProductType.Broadband:
        return 'Sorry, our broadband is currently unavailable in your area';
      case ProductType.Mobile:
        return 'Sorry, our mobile is currently unavailable in your area';
      case ProductType.Insurance:
        return 'Sorry, our insurance is currently unavailable in your area';
      case ProductType.CashbackCard:
        return 'Sorry, our cashback card is currently unavailable in your area';
      default:
        return null;
    }
  }
  switch (productType) {
    case ProductType.Energy:
      return 'Always cheaper than the price cap';
    case ProductType.Broadband:
      return isNewCopy && tbybEligible
        ? '6 months free. Up to 900Mbps. £0 Setup.'
        : 'Superfast speeds with no connection fee';
    case ProductType.Mobile:
      return isNewCopy
        ? 'The UK’s cheapest unlimited data SIM, with up to 3 extra SIMs free for 3 months.'
        : '99% coverage on the UK’s best network';
    case ProductType.Insurance:
      if (subType === ProductSubtype.BoilerHomeCover)
        return 'Moneyfacts 5-star rated cover, with an annual boiler service, 24/7 claims team and cover for your home emergencies';
      if (subType === ProductSubtype.IncomeProtector)
        return 'Help replace some of your earnings if you’re unable to work through no fault of your own';

      return 'Protect your boiler and/or income';
    case ProductType.CashbackCard:
      return 'Earn up to 15% cashback every time you use your card online and in-store';
    default:
      return null;
  }
};

const Description = ({
  productType,
  subType,
  isSelected,
  price,
  disabled,
  discount,
  compact,
  showMonthLabel,
  renderDiscount,
  tbybEligible,
  bundleCopyVariant,
}: DescriptionProps) => {
  if (isSelected) {
    const hasShortLabel = productType !== ProductType.Energy;

    return (
      <div
        className={clsx('bundle-tile__price', {
          'bundle-tile__price--short-label': hasShortLabel,
          'bundle-tile__price--compact': compact,
        })}
      >
        {renderDiscount ? (
          renderDiscount()
        ) : (
          <>
            <DetailText size="2xl" color={discount ? 'valid' : 'default'}>
              {discount
                ? formatCurrency(discount.price)
                : formatCurrency(price)}
            </DetailText>
            {discount?.durationMonths && discount.durationMonths > 0 ? (
              <BodyText
                data-testid="bundle-tile__discount"
                as="span"
                textAlign="left"
              >
                for {discount.durationMonths} months,{' '}
                <br className="bundle-tile__discount-break" />
                then <Strong>{formatCurrency(price)}</Strong>{' '}
                {showMonthLabel && 'a month'}
              </BodyText>
            ) : (
              <BodyText as="span" textAlign="right" size="sm">
                {hasShortLabel ? 'a month' : 'monthly estimate'}
              </BodyText>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <BodyText size="md" color="secondary" className="bundle-tile__description">
      {getDescription(productType, {
        subType,
        disabled,
        tbybEligible,
        bundleCopyVariant,
      })}
    </BodyText>
  );
};

const getPath = (
  productType: ProductType,
  routeToEnergyForm: boolean,
  subType?: ProductSubtype
) => {
  switch (productType) {
    case ProductType.Energy:
      return routeToEnergyForm ? paths.energyDetails : paths.energy;

    case ProductType.Broadband:
      return paths.broadband;
    case ProductType.Mobile:
      return paths.mobile;
    case ProductType.Insurance:
      if (subType === ProductSubtype.BoilerHomeCover) return paths.bhc;
      if (subType === ProductSubtype.IncomeProtector) return paths.ip;

      return paths.insurance;
    case ProductType.CashbackCard:
      return paths.cashback;
    default:
      return paths.bundle;
  }
};

const BundleTile = ({
  isSelected,
  showCheaperBadge,
  productType,
  subType,
  price,
  disabled,
  discount,
  compact,
  showMonthLabel,
  tbybEligible,
  bundleCopyVariant,
  onClick,
  renderDiscount,
}: BundleTileProps) => {
  const navigate = useNavigate();
  const { activeClient } = useAuthContext();
  const { appointment } = useAppointmentContext();

  const authenticated = activeClient !== null;
  const routeToEnergyForm = !isSelected && (authenticated || !!appointment);
  const path = getPath(productType, routeToEnergyForm, subType);
  const displayCheaperBadge = !isSelected && showCheaperBadge;

  const handleClick = () => {
    if (path) {
      navigate(path);
    }
    if (onClick) {
      onClick();
    }
  };

  if (disabled) {
    return (
      <Card
        data-testid={`bundle-tile-${subType ?? productType}`}
        className={clsx(
          'bundle-tile__wrapper',
          'bundle-tile__wrapper--disabled'
        )}
        variant="subtle"
        tabIndex={0}
        onClick={noop}
        paddingNone
      >
        <ProductIndicator productType={productType} subType={subType} />
        <Flex
          justifyContent="between"
          direction="column"
          className={clsx(
            'bundle-tile__content',
            'bundle-tile__content--disabled'
          )}
        >
          <Heading
            className="bundle-tile__disabled-title"
            as="h3"
            margin="0"
            size="lg"
          >
            {getProductLabel(productType, subType)}
          </Heading>
          <div>
            <BodyText
              size="md"
              color="secondary"
              className="bundle-tile__description"
            >
              {getDescription(productType, { subType, disabled })}
            </BodyText>
          </div>
          <div />
        </Flex>
      </Card>
    );
  }

  return (
    <Card
      data-testid={`bundle-tile-${subType ?? productType}`}
      className={clsx('bundle-tile__wrapper', {
        'bundle-tile__wrapper--selected': isSelected,
      })}
      variant={isSelected ? 'emphasis' : 'subtle'}
      tabIndex={0}
      onClick={handleClick}
      paddingNone
    >
      <ProductIndicator productType={productType} subType={subType} />
      <Flex
        justifyContent="between"
        direction="column"
        className="bundle-tile__content"
      >
        <Flex
          alignItems="start"
          justifyContent="between"
          className={clsx('bundle-tile__header', {
            'bundle-tile__header--compact': compact,
          })}
        >
          <Heading as="h3" margin="0" size="lg">
            {getProductLabel(productType, subType)}
          </Heading>
          {displayCheaperBadge && (
            <Badge
              size="sm"
              colorScheme="highlight"
              className="bundle-tile__cheaper-mobile-badge"
            >
              Cheaper prices unlocked
            </Badge>
          )}
          <Description
            productType={productType}
            subType={subType}
            isSelected={isSelected}
            price={price}
            discount={discount}
            compact={compact}
            showMonthLabel={showMonthLabel}
            tbybEligible={tbybEligible}
            bundleCopyVariant={bundleCopyVariant}
            renderDiscount={renderDiscount}
          />
        </Flex>
        <div
          className={clsx('bundle-tile__footer', {
            'bundle-tile__footer--compact': compact,
          })}
        >
          <CardInteraction asChild>
            <Link asChild>
              <button type="button">
                {isSelected ? 'Edit' : 'Find out more'}
                <ChevronRightSmallIcon />
              </button>
            </Link>
          </CardInteraction>
          {isSelected && (
            <Badge size="sm" colorScheme="positive">
              <TickSmallIcon />
              In your bundle
            </Badge>
          )}
          {displayCheaperBadge && (
            <Badge
              className="bundle-tile__cheaper-desktop-badge"
              colorScheme="highlight"
              size="sm"
            >
              Cheaper prices unlocked
            </Badge>
          )}
        </div>
      </Flex>
    </Card>
  );
};

export { BundleTile };
